import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useContent } from '../../context/ContentContext';
import { Target, Lightbulb, Users, Award, Star, CheckCircle } from 'lucide-react';

const ICON_MAP = { target: Target, lightbulb: Lightbulb, users: Users, award: Award, star: Star };

const AboutModal = ({ data }) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';

  const values = siteContent?.about?.values || [];
  const value = values.find(v => v.id === data?.key) || {};

  const IconComponent = ICON_MAP[value.icon] || Target;
  const title = data?.title || (language === 'ar' ? value.title_ar : value.title_en) || '';
  const description = data?.description || (language === 'ar' ? value.description_ar : value.description_en) || '';
  const badge = (language === 'ar' ? value.badge_ar : value.badge_en) || '';
  const tagline = (language === 'ar' ? value.tagline_ar : value.tagline_en) || '';

  const timeline = (value.timeline || []).filter(item => {
    const event = language === 'ar' ? item.event_ar : item.event_en;
    return (item.year && String(item.year).trim()) || (event && String(event).trim());
  });
  const highlightsList = language === 'ar' ? (value.highlights_ar || []) : (value.highlights_en || []);
  const highlights = highlightsList.filter(h => h && String(h).trim());

  const hasSideContent = timeline.length > 0 || highlights.length > 0;
  const sideGridCols = (timeline.length > 0 && highlights.length > 0) ? 'sm:grid-cols-2' : 'sm:grid-cols-1';

  const milestonesLabel = language === 'ar' ? 'أبرز الأرقام' : 'Key Milestones';
  const highlightsLabel = language === 'ar' ? 'نقاط التميز' : 'Highlights';

  return (
    <div className={`p-6 sm:p-10 ${isRtl ? 'text-right' : 'text-left'}`}>
      <div className={`flex items-center gap-3 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <div className={`w-12 h-12 flex items-center justify-center rounded-sm ${isDark ? 'bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10'}`}>
          <IconComponent className="w-6 h-6 text-[var(--gold-primary)]" />
        </div>
        <div>
          {badge && (
            <span className="text-[var(--gold-primary)] text-xs uppercase tracking-widest font-semibold block">
              {badge}
            </span>
          )}
          <h2 className={`font-display text-2xl sm:text-3xl font-bold mt-0.5 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {title}
          </h2>
        </div>
      </div>

      {tagline && (
        <p className={`text-base italic mb-6 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>{tagline}</p>
      )}

      {description && (
        <p className={`text-base sm:text-lg leading-relaxed mb-10 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
          {description}
        </p>
      )}

      {hasSideContent && (
        <div className={`grid ${sideGridCols} gap-8`}>
          {timeline.length > 0 && (
            <div>
              <h4 className={`text-xs uppercase tracking-widest font-semibold text-[var(--gold-primary)] mb-5`}>
                {milestonesLabel}
              </h4>
              <div className="space-y-3">
                {timeline.map((item, i) => {
                  const event = language === 'ar' ? item.event_ar : item.event_en;
                  return (
                    <div
                      key={i}
                      className={`flex items-start gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                      <span className={`font-display text-sm font-bold text-[var(--gold-primary)] min-w-[3.5rem] ${isRtl ? 'text-left' : 'text-right'}`}>
                        {item.year}
                      </span>
                      <div className="flex-1 flex items-start gap-2 pt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)]/50 mt-1.5 flex-shrink-0 ${isRtl ? 'order-last' : ''}`} />
                        <span className={`text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{event}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {highlights.length > 0 && (
            <div>
              <h4 className={`text-xs uppercase tracking-widest font-semibold text-[var(--gold-primary)] mb-5`}>
                {highlightsLabel}
              </h4>
              <ul className="space-y-3">
                {highlights.map((h, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 text-sm ${isDark ? 'text-white/75' : 'text-gray-600'} ${isRtl ? 'flex-row-reverse' : ''}`}
                  >
                    <CheckCircle className="w-4 h-4 text-[var(--gold-primary)] flex-shrink-0 mt-0.5" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className={`mt-10 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
        <a
          href="#about"
          onClick={(e) => { e.preventDefault(); document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' }); }}
          className={`text-sm font-medium text-[var(--gold-primary)] hover:underline`}
        >
          {language === 'ar' ? '← اقرأ المزيد عن قصتنا' : '← Read more about our story'}
        </a>
      </div>
    </div>
  );
};

export default AboutModal;
